import { Fragment, useState, type ReactNode } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";

import { useSavedViews } from "@features/sales-v3/hooks";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";

import { CreateSavedViewsModal } from "./create-saved-views-modal";
import { UpdateSavedViewsModal } from "./update-saved-views-modal";

export const SaveSavedViewsMenu = () => {
  const [isUpdatingSavedView, setIsUpdatingSavedView] = useState(false);
  const [isCreatingSavedView, setIsCreatingSavedView] = useState(false);
  const { selectedSavedView, isStateSaved } = useSavedViews();

  return (
    <div className="relative">
      <Menu>
        {({ open }) => (
          <>
            <MenuButton
              as="button"
              className={clsx("button secondary-button sm font-bold", {
                "text-white bg-cosmic border-cosmic": open,
              })}
            >
              SAVE
              <motion.i
                initial={false}
                animate={{ rotate: open ? 0 : 180 }}
                transition={{ duration: 0.2 }}
                className={clsx("fa-regular fa-sharp fa-chevron-up ml-2", {
                  "text-white": open,
                })}
              />
            </MenuButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <MenuItems
                className={clsx(
                  "absolute z-10 left-0 bg-white rounded-lg elevation-1",
                  "text-sm pt-1 pb-2 w-40",
                )}
              >
                {!selectedSavedView?.is_default && (
                  <Option
                    isDisabled={isStateSaved}
                    onClick={() => {
                      setIsUpdatingSavedView(true);
                    }}
                  >
                    Update existing
                  </Option>
                )}
                <Option onClick={() => setIsCreatingSavedView(true)}>
                  Save as new
                </Option>
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>
      <UpdateSavedViewsModal
        isOpen={isUpdatingSavedView}
        onConfirm={() => setIsUpdatingSavedView(false)}
        onCancel={() => setIsUpdatingSavedView(false)}
      />
      <CreateSavedViewsModal
        isOpen={isCreatingSavedView}
        onConfirm={() => setIsCreatingSavedView(false)}
        onCancel={() => setIsCreatingSavedView(false)}
      />
    </div>
  );
};

const Option = ({
  children,
  isDisabled = false,
  onClick,
}: {
  children: ReactNode;
  isDisabled?: boolean;
  onClick?(): void;
}) => (
  <MenuItem>
    {({ focus }) => {
      return (
        <div
          className={clsx("w-full text-left px-4 h-8 flex items-center", {
            "cursor-pointer": !isDisabled,
            "opacity-50": isDisabled,
            "bg-cosmic-4": focus,
          })}
          onClick={() => {
            if (!isDisabled) {
              onClick && onClick();
            }
          }}
        >
          {children}
        </div>
      );
    }}
  </MenuItem>
);
